// "Showing 21–40 of 57 · ‹ Prev  Page 2 of 3  Next ›" under a paged list.
// Works for lists paged here (slice) and on the server (total from the
// response). Use with usePagedList for the slicing kind.
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Props = {
    page: number;          // 1-based
    pageSize: number;
    total: number;
    onPage: (page: number) => void;
    loading?: boolean;
    className?: string;
};

export function DataPager({ page, pageSize, total, onPage, loading, className }: Props) {
    const pages = Math.max(1, Math.ceil(total / pageSize));
    const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);
    return (
        <div className={cn("flex items-center justify-between gap-3", className)}>
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
                {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {total === 0 ? "Nothing to show" : `Showing ${from}–${to} of ${total}`}
            </p>
            <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-8 gap-1" disabled={loading || page <= 1} onClick={() => onPage(page - 1)}>
                    <ChevronLeft className="h-4 w-4" /> Prev
                </Button>
                <span className="whitespace-nowrap text-xs text-muted-foreground">Page {Math.min(page, pages)} of {pages}</span>
                <Button variant="outline" size="sm" className="h-8 gap-1" disabled={loading || page >= pages} onClick={() => onPage(page + 1)}>
                    Next <ChevronRight className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
}

// Search + paging over a list already in memory. `match` decides whether an
// item fits the (lower-cased, trimmed) query; a new query goes back to page 1.
export function usePagedList<T>(items: T[], match: (item: T, q: string) => boolean, pageSize = 20) {
    const [ query, setQuery ] = useState("");
    const [ page, setPage ] = useState(1);
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? items.filter(i => match(i, q)) : items;
    }, [ items, query ]);
    useEffect(() => { setPage(1); }, [ query ]);
    const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const current = Math.min(page, pages);
    return {
        query, setQuery,
        page: current, setPage, pageSize,
        total: filtered.length,
        rows: filtered.slice((current - 1) * pageSize, current * pageSize),
    };
}
